/* eslint-disable no-unused-vars */
import { useEffect, useState, useCallback, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import { ArrowLeft, RefreshCw, CheckCircle, Send, Eye } from 'lucide-react';
import { BLOCOS, OPCOES_ESCALA } from './RHFormsPesquisaExperienciaColaborador_Externo';
import { getEmpresa } from '../../../utils/empresas';

const STATUS_LABELS = {
    respondido: 'Respondido',
    enviado: 'Aguardando',
    expirado: 'Expirado',
    pendente: 'Não Enviado',
};

const STATUS_COLORS = {
    respondido: { bg: '#d1fae5', color: '#065f46' },
    enviado: { bg: '#dbeafe', color: '#1e40af' },
    expirado: { bg: '#fee2e2', color: '#991b1b' },
    pendente: { bg: '#f1f5f9', color: '#475569' },
};

const calcularStatus = (row) => {
    if (row.RESPONDIDO) return 'respondido';
    if (!row.DATA_ENVIO) return 'pendente';
    if (row.DATA_EXPIRACAO && new Date(row.DATA_EXPIRACAO) < new Date()) return 'expirado';
    return 'enviado';
};

function PesquisaClimaConsulta({ embedded = false }) {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isAdminOrRH = user.perfil === 'Admin' || user.perfil === 'RH';
    const isAdmin = user.perfil === 'Admin';

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);

    const [statusPesquisa, setStatusPesquisa] = useState(null);
    const [funcionarios, setFuncionarios] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingAcao, setLoadingAcao] = useState(false);
    const [loadingDisparo, setLoadingDisparo] = useState(false);
    const [msgDisparo, setMsgDisparo] = useState(null);
    const [lojasDisparo, setLojasDisparo] = useState([]); // centros de custo selecionados p/ disparo (vazio = todos permitidos)
    const [showPreview, setShowPreview] = useState(false);
    const [error, setError] = useState(null);

    const [filterLoja, setFilterLoja] = useState('');
    const [agruparPor, setAgruparPor] = useState('loja'); // 'loja' | 'empresa'

    const getMarginLeft = () => {
        if (window.innerWidth <= 480) return isCollapsed ? '60px' : '0px';
        return isCollapsed ? '100px' : '260px';
    };

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Filtragem reativa
    useEffect(() => {
        let result = [...funcionarios];
        if (filterLoja) {
            result = result.filter(r => r.LOJA === filterLoja);
        }
        setFiltered(result);
    }, [filterLoja, funcionarios]);

    const fetchDados = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('authToken');
            const [resStatus, resFuncionarios] = await Promise.all([
                fetch('/api/forms-rh/pesquisaclima/status', { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch('/api/forms-rh/pesquisaclima/pendentes', { headers: { 'Authorization': `Bearer ${token}` } })
            ]);
            if (!resStatus.ok || !resFuncionarios.ok) throw new Error('Falha ao carregar dados.');
            setStatusPesquisa(await resStatus.json());
            const funcData = await resFuncionarios.json();
            setFuncionarios(funcData);
            setFiltered(funcData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchDados(); }, [fetchDados]);

    const handleAtivar = async () => {
        if (!window.confirm('Deseja ativar uma nova Pesquisa de Experiência do Colaborador? O período de coleta será de 30 dias a partir de hoje.')) return;
        setLoadingAcao(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch('/api/forms-rh/pesquisaclima/config/ativar', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            alert(data.message);
            fetchDados();
        } catch (err) {
            alert('Erro: ' + err.message);
        } finally {
            setLoadingAcao(false);
        }
    };

    const handleDesativar = async () => {
        if (!window.confirm('Deseja encerrar a Pesquisa de Experiência do Colaborador antecipadamente? Esta ação não pode ser desfeita.')) return;
        setLoadingAcao(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch('/api/forms-rh/pesquisaclima/config/desativar', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            alert(data.message);
            fetchDados();
        } catch (err) {
            alert('Erro: ' + err.message);
        } finally {
            setLoadingAcao(false);
        }
    };

    const handleDispararAgora = async () => {
        if (!window.confirm('Deseja disparar o envio de WhatsApp agora para todos os funcionários pendentes?')) return;
        setLoadingDisparo(true);
        setMsgDisparo(null);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch('/api/forms-rh/pesquisaclima/disparar-agora', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            setMsgDisparo({ tipo: 'sucesso', texto: '✅ Disparo iniciado! Acompanhe o log do servidor para verificar os envios.' });
            setTimeout(() => fetchDados(), 5000);
        } catch (err) {
            setMsgDisparo({ tipo: 'erro', texto: '❌ Erro: ' + err.message });
        } finally {
            setLoadingDisparo(false);
        }
    };

    const lojas = [...new Set(funcionarios.map(f => f.LOJA))].sort();

    // Disparo por loja (centro de custo): reenvia o mesmo link a quem não respondeu / cria p/ quem não tem.
    // lojasDisparo vazio => backend dispara para todos os centros permitidos ao usuário.
    const toggleLojaDisparo = (loja) => setLojasDisparo(prev =>
        prev.includes(loja) ? prev.filter(l => l !== loja) : [...prev, loja]
    );
    const handleDispararPorLoja = async () => {
        const alvoTexto = lojasDisparo.length ? lojasDisparo.join(', ') : 'TODAS as lojas disponíveis';
        if (!window.confirm(`Disparar a Pesquisa por WhatsApp para: ${alvoTexto}?\n\nReenvia o MESMO link a quem ainda não respondeu (não duplica) e cria para quem ainda não tem.`)) return;
        setLoadingDisparo(true);
        setMsgDisparo(null);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch('/api/forms-rh/pesquisaclima/reenviar-por-loja', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ lojas: lojasDisparo })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            setMsgDisparo({ tipo: 'sucesso', texto: `✅ ${data.message}` });
            setTimeout(() => fetchDados(), 6000);
        } catch (err) {
            setMsgDisparo({ tipo: 'erro', texto: '❌ Erro: ' + err.message });
        } finally {
            setLoadingDisparo(false);
        }
    };

    // Auditoria de entrega (caça-429): checa no ManyChat quem NÃO recebeu e reenvia só para esses,
    // no escopo de loja do usuário. Não duplica nem reenvia a quem já recebeu/respondeu.
    const handleAuditoriaReenvio = async () => {
        if (!window.confirm('Verificar quem NÃO recebeu (erro 429) e reenviar apenas para esses, no seu escopo de loja?\n\nNão duplica e não reenvia para quem já recebeu/respondeu.')) return;
        setLoadingDisparo(true);
        setMsgDisparo(null);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch('/api/forms-rh/pesquisaclima/auditoria-reenvio', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            setMsgDisparo({ tipo: 'sucesso', texto: `✅ ${data.message}` });
            setTimeout(() => fetchDados(), 8000);
        } catch (err) {
            setMsgDisparo({ tipo: 'erro', texto: '❌ Erro: ' + err.message });
        } finally {
            setLoadingDisparo(false);
        }
    };

    // Agrega por loja — sem expor dados individuais
    const porLoja = useMemo(() => {
        const map = {};
        filtered.forEach(row => {
            if (!map[row.LOJA]) {
                map[row.LOJA] = { LOJA: row.LOJA, EMPRESA: getEmpresa(row.LOJA), total: 0, respondidos: 0, aguardando: 0, expirados: 0, pendentes: 0 };
            }
            const s = calcularStatus(row);
            map[row.LOJA].total++;
            if (s === 'respondido') map[row.LOJA].respondidos++;
            else if (s === 'enviado')  map[row.LOJA].aguardando++;
            else if (s === 'expirado') map[row.LOJA].expirados++;
            else                       map[row.LOJA].pendentes++;
        });
        return Object.values(map).sort((a, b) => a.LOJA.localeCompare(b.LOJA));
    }, [filtered]);

    // Agrega por empresa — mesma contagem, agrupando as lojas pela empresa
    const porEmpresa = useMemo(() => {
        const map = {};
        filtered.forEach(row => {
            const empresa = getEmpresa(row.LOJA);
            if (!map[empresa]) {
                map[empresa] = { EMPRESA: empresa, total: 0, respondidos: 0, aguardando: 0, expirados: 0, pendentes: 0 };
            }
            const s = calcularStatus(row);
            map[empresa].total++;
            if (s === 'respondido') map[empresa].respondidos++;
            else if (s === 'enviado')  map[empresa].aguardando++;
            else if (s === 'expirado') map[empresa].expirados++;
            else                       map[empresa].pendentes++;
        });
        return Object.values(map).sort((a, b) => a.EMPRESA.localeCompare(b.EMPRESA));
    }, [filtered]);

    const porEmpresaAtivo = agruparPor === 'empresa';
    const dados = porEmpresaAtivo ? porEmpresa : porLoja;

    const inputStyle = { padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', backgroundColor: '#fff', color: '#334155', outline: 'none' };
    const btnBase = { padding: '10px 20px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'opacity 0.15s' };

    // --- Modal de preview do formulário ---
    const ModalPreview = () => {
        const [respostas, setRespostas] = useState(() => {
            const e = {};
            BLOCOS.forEach(b => b.perguntas.forEach(p => { e[p.id] = ''; }));
            return e;
        });
        const totalPerguntas = Object.keys(respostas).length;
        const totalRespondidas = Object.values(respostas).filter(v => v).length;
        const progressoPct = Math.round((totalRespondidas / totalPerguntas) * 100);

        return (
            <div style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                backgroundColor: 'rgba(0,0,0,0.55)',
                display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
                overflowY: 'auto', padding: '32px 16px'
            }}
                onClick={(e) => { if (e.target === e.currentTarget) setShowPreview(false); }}
            >
                <div style={{ width: '100%', maxWidth: '720px', position: 'relative' }}>

                    {/* Barra de controle do preview */}
                    <div style={{
                        backgroundColor: '#1e1b4b', borderRadius: '12px 12px 0 0',
                        padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#fff', fontWeight: 'bold', fontSize: '14px' }}>
                            <Eye size={16} /> Modo Visualização — Formulário da Pesquisa de Experiência do Colaborador                        </div>
                        <button onClick={() => setShowPreview(false)} style={{
                            background: 'none', border: 'none', color: '#a5b4fc',
                            cursor: 'pointer', fontSize: '20px', lineHeight: 1
                        }}>✕</button>
                    </div>

                    {/* Conteúdo do formulário */}
                    <div style={{ backgroundColor: '#f1f5f9', borderRadius: '0 0 12px 12px', padding: '32px 20px' }}>

                        {/* Logo */}
                        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
                            <img alt="Grupo Correia" src="/icon-logotipo.png" style={{ height: '200px', objectFit: 'contain' }} />
                        </div>

                        {/* Cabeçalho informativo */}
                        <div style={{ backgroundColor: '#f3f0ff', borderLeft: '4px solid #7c3aed', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#5b21b6', margin: 0 }}>Informações sobre a Pesquisa</h2>
                                <span style={{ color: '#7c3aed', fontWeight: 'bold' }}>▲</span>
                            </div>
                            <div style={{ marginTop: '14px', color: '#4c1d95', fontSize: '14px', lineHeight: 1.7 }}>
                                <p style={{ margin: '0 0 10px 0' }}><strong>Sua participação é muito importante!</strong></p>
                                <ul style={{ paddingLeft: '20px', margin: '0 0 10px 0' }}>
                                    <li><strong>Pesquisa anônima:</strong> Suas respostas são 100% confidenciais. Nenhum dado individual será identificado.</li>
                                    <li><strong>Objetivo:</strong> Entender melhor a experiência e a percepção da equipe sobre o dia a dia no trabalho, contribuindo para melhorias contínuas no ambiente e na rotina.</li>
                                    <li><strong>Sigilo:</strong> Os resultados são analisados apenas de forma coletiva, nunca individualmente.</li>
                                </ul>
                                <p style={{ margin: 0, borderTop: '1px solid #ddd6fe', paddingTop: '10px' }}>
                                    <strong>Orientação:</strong> Responda pensando na sua rotina habitual. Não existem respostas certas ou erradas — queremos conhecer sua percepção real.
                                </p>
                            </div>
                        </div>

                        {/* Barra de progresso */}
                        <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', color: '#64748b' }}>
                                <span>Progresso</span>
                                <span style={{ fontWeight: 'bold', color: progressoPct === 100 ? '#10b981' : '#475569' }}>
                                    {totalRespondidas} / {totalPerguntas} perguntas respondidas
                                </span>
                            </div>
                            <div style={{ backgroundColor: '#e2e8f0', borderRadius: '99px', height: '10px', overflow: 'hidden' }}>
                                <div style={{ width: `${progressoPct}%`, height: '100%', backgroundColor: progressoPct === 100 ? '#10b981' : '#7c3aed', borderRadius: '99px', transition: 'width 0.3s ease' }} />
                            </div>
                        </div>

                        {/* Blocos de perguntas */}
                        {BLOCOS.map((bloco, blocoIdx) => {
                            const todasRespondidas = bloco.perguntas.every(p => respostas[p.id]);
                            return (
                                <div key={blocoIdx} style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                    {todasRespondidas && (
                                        <div style={{ textAlign: 'right', marginBottom: '12px' }}>
                                            <span style={{ color: '#10b981', fontSize: '20px' }}>✓</span>
                                        </div>
                                    )}
                                    {bloco.perguntas.map((pergunta, pIdx) => {
                                        const numero = BLOCOS.slice(0, blocoIdx).reduce((acc, b) => acc + b.perguntas.length, 0) + pIdx + 1;
                                        const valorAtual = respostas[pergunta.id];
                                        return (
                                            <div key={pergunta.id} style={{ marginBottom: pIdx < bloco.perguntas.length - 1 ? '28px' : '4px' }}>
                                                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '12px' }}>
                                                    <span style={{ color: '#7c3aed', marginRight: '6px' }}>{numero}.</span>
                                                    {pergunta.texto}
                                                </label>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px' }}>
                                                    {OPCOES_ESCALA.map(opcao => {
                                                        const selecionado = valorAtual === opcao;
                                                        return (
                                                            <Fragment key={opcao}>
                                                                <input type="radio" id={`prev-${pergunta.id}-${opcao}`} name={`prev-${pergunta.id}`} value={opcao} checked={selecionado}
                                                                    onChange={() => setRespostas(prev => ({ ...prev, [pergunta.id]: opcao }))}
                                                                    style={{ display: 'none' }}
                                                                />
                                                                <label htmlFor={`prev-${pergunta.id}-${opcao}`} style={{
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    padding: '10px 4px', fontSize: '11px', textAlign: 'center', lineHeight: '1.3',
                                                                    backgroundColor: selecionado ? '#7c3aed' : '#f8fafc',
                                                                    color: selecionado ? '#fff' : '#475569',
                                                                    border: `2px solid ${selecionado ? '#7c3aed' : '#e2e8f0'}`,
                                                                    borderRadius: '8px', cursor: 'pointer',
                                                                    fontWeight: selecionado ? 'bold' : 'normal',
                                                                    transition: 'all 0.15s ease', minHeight: '52px'
                                                                }}>
                                                                    {opcao}
                                                                </label>
                                                            </Fragment>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}

                        {/* Botão de envio desativado */}
                        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '14px', marginBottom: '16px', fontSize: '13px', color: '#92400e', textAlign: 'center' }}>
                            👁️ Modo de visualização — o envio está desativado
                        </div>
                        <button disabled style={{
                            width: '100%', padding: '16px', backgroundColor: '#94a3b8',
                            color: '#fff', fontSize: '16px', fontWeight: 'bold',
                            border: 'none', borderRadius: '8px', cursor: 'not-allowed', marginBottom: '16px'
                        }}>
                            Enviar Pesquisa
                        </button>
                        <button onClick={() => setShowPreview(false)} style={{
                            width: '100%', padding: '14px', backgroundColor: '#1e1b4b',
                            color: '#fff', fontSize: '14px', fontWeight: 'bold',
                            border: 'none', borderRadius: '8px', cursor: 'pointer', marginBottom: '20px'
                        }}>
                            Fechar Visualização
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f1f5f9', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            {showPreview && <ModalPreview />}
            {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

            <main style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), padding: isMobile ? '16px' : '32px', transition: 'margin-left 0.3s ease', minWidth: 0 }}>

                {/* Cabeçalho */}
                {!embedded && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                        <button onClick={() => navigate('/rh')} style={{ ...btnBase, backgroundColor: '#fff', color: '#475569', border: '1px solid #e2e8f0', padding: '8px 14px' }}>
                            <ArrowLeft size={16} /> Voltar
                        </button>
                        <div>
                            <h1 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>Pesquisa de Experiência do Colaborador</h1>
                            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Controle de envio e monitoramento de respostas</p>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Carregando...</div>
                ) : error ? (
                    <div style={{ backgroundColor: '#fef2f2', color: '#991b1b', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>{error}</div>
                ) : (
                    <>
                        {/* Painel de Status */}
                        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>

                                {/* Status e progresso */}
                                <div style={{ flex: 1, minWidth: '220px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                        <span style={{
                                            display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%',
                                            backgroundColor: statusPesquisa?.ativa ? '#10b981' : '#94a3b8'
                                        }} />
                                        <span style={{ fontWeight: 'bold', color: '#0f172a', fontSize: '16px' }}>
                                            {statusPesquisa?.ativa ? 'Pesquisa Ativa' : 'Pesquisa Inativa'}
                                        </span>
                                    </div>

                                    {statusPesquisa?.ativa && (
                                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px' }}>
                                            <div>Início: <strong>{new Date(statusPesquisa.data_inicio).toLocaleDateString('pt-BR')}</strong></div>
                                            <div>Encerramento: <strong>{new Date(statusPesquisa.data_fim).toLocaleDateString('pt-BR')}</strong></div>
                                        </div>
                                    )}

                                    {/* Barra de progresso da meta */}
                                    <div style={{ marginBottom: '6px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                                            <span style={{ color: '#64748b' }}>Respostas coletadas</span>
                                            <span style={{ fontWeight: 'bold', color: statusPesquisa?.metaAtingida ? '#10b981' : '#334155' }}>
                                                {statusPesquisa?.totalRespondidos || 0} / {statusPesquisa?.totalElegiveis || 0}
                                                &nbsp;({statusPesquisa?.percentual || 0}%)
                                            </span>
                                        </div>
                                        <div style={{ backgroundColor: '#e2e8f0', borderRadius: '99px', height: '12px', overflow: 'hidden' }}>
                                            <div style={{
                                                width: `${Math.min(statusPesquisa?.percentual || 0, 100)}%`,
                                                height: '100%',
                                                backgroundColor: statusPesquisa?.metaAtingida ? '#10b981' : '#7c3aed',
                                                borderRadius: '99px',
                                                transition: 'width 0.4s ease'
                                            }} />
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                                            Meta: {statusPesquisa?.meta || 51}%
                                        </div>
                                    </div>

                                    {statusPesquisa?.metaAtingida && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10b981', fontWeight: 'bold', fontSize: '13px', marginTop: '8px' }}>
                                            <CheckCircle size={16} /> Meta de {statusPesquisa?.meta}% atingida!
                                        </div>
                                    )}
                                </div>

                                {/* Cards de contagem por loja */}
                                {statusPesquisa?.porLoja?.length > 0 && (
                                    <div style={{ flex: 1, minWidth: '180px' }}>
                                        <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                                            Respostas por Loja
                                        </p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                            {statusPesquisa.porLoja.map(l => (
                                                <div key={l.LOJA} style={{ backgroundColor: '#f3f0ff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '8px 14px', fontSize: '13px' }}>
                                                    <span style={{ fontWeight: 'bold', color: '#5b21b6' }}>{l.LOJA}</span>
                                                    <span style={{ color: '#7c3aed', marginLeft: '6px' }}>{l.RESPONDIDOS}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Ações (só Admin/RH) */}
                                {isAdminOrRH && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '160px' }}>
                                        {!statusPesquisa?.ativa ? (
                                            <button onClick={handleAtivar} disabled={loadingAcao} style={{ ...btnBase, backgroundColor: '#10b981', color: '#fff' }}>
                                                <Send size={15} /> {loadingAcao ? 'Ativando...' : 'Ativar Pesquisa'}
                                            </button>
                                        ) : (
                                            <button onClick={handleDesativar} disabled={loadingAcao} style={{ ...btnBase, backgroundColor: '#ef4444', color: '#fff' }}>
                                                {loadingAcao ? 'Encerrando...' : 'Encerrar Pesquisa'}
                                            </button>
                                        )}

                                        {/* Disparo por loja (centro de custo) — Admin/RH/Diretoria, com pesquisa ativa.
                                            Reusa o mesmo link de quem não respondeu (não duplica) e cria p/ quem não tem. */}
                                        {statusPesquisa?.ativa && lojas.length > 0 && (
                                            <button
                                                onClick={handleDispararPorLoja}
                                                disabled={loadingDisparo}
                                                title="Dispara/reenvia a Pesquisa por WhatsApp nas lojas marcadas abaixo (ou em todas as disponíveis, se nenhuma marcada). Reenvia o mesmo link a quem ainda não respondeu — não duplica."
                                                style={{ ...btnBase, backgroundColor: '#7c3aed', color: '#fff', opacity: loadingDisparo ? 0.7 : 1 }}
                                            >
                                                <Send size={15} /> {loadingDisparo ? 'Disparando...' : `Disparar por loja${lojasDisparo.length ? ` (${lojasDisparo.length})` : ' (todas)'}`}
                                            </button>
                                        )}

                                        {/* Auditoria de entrega (caça-429): reenvia só para quem não recebeu, no escopo da loja */}
                                        {statusPesquisa?.ativa && lojas.length > 0 && (
                                            <button
                                                onClick={handleAuditoriaReenvio}
                                                disabled={loadingDisparo}
                                                title="Verifica no ManyChat quem NÃO recebeu (erro 429) e reenvia apenas para esses, no seu escopo de loja. Não duplica nem reenvia a quem já recebeu/respondeu."
                                                style={{ ...btnBase, backgroundColor: '#f59e0b', color: '#fff', opacity: loadingDisparo ? 0.7 : 1 }}
                                            >
                                                <RefreshCw size={14} /> Recuperar não-recebidos (429)
                                            </button>
                                        )}

                                        <button onClick={() => setShowPreview(true)} style={{ ...btnBase, backgroundColor: '#f8fafc', color: '#5b21b6', border: '1px solid #ddd6fe' }}>
                                            <Eye size={14} /> Visualizar Formulário
                                        </button>
                                        <button onClick={fetchDados} style={{ ...btnBase, backgroundColor: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}>
                                            <RefreshCw size={14} /> Atualizar
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Seleção de lojas (centro de custo) para o disparo — vazio = todas as disponíveis */}
                        {statusPesquisa?.ativa && lojas.length > 0 && (
                            <div style={{ backgroundColor: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 600, color: '#5b21b6', marginBottom: '8px' }}>
                                    Lojas para o disparo {lojasDisparo.length === 0 && <span style={{ fontWeight: 400, color: '#7c3aed' }}>(nenhuma marcada = todas as disponíveis)</span>}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                                    {lojas.map(l => (
                                        <label key={l} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#475569', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={lojasDisparo.includes(l)} onChange={() => toggleLojaDisparo(l)} />
                                            {l}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Feedback do disparo manual */}
                        {msgDisparo && (
                            <div style={{
                                backgroundColor: msgDisparo.tipo === 'sucesso' ? '#ecfdf5' : '#fef2f2',
                                border: `1px solid ${msgDisparo.tipo === 'sucesso' ? '#6ee7b7' : '#fca5a5'}`,
                                borderRadius: '8px', padding: '14px', marginBottom: '16px',
                                fontSize: '13px', color: msgDisparo.tipo === 'sucesso' ? '#065f46' : '#991b1b',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                            }}>
                                <span>{msgDisparo.texto}</span>
                                <button onClick={() => setMsgDisparo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#94a3b8' }}>✕</button>
                            </div>
                        )}

                        {/* Aviso: pesquisa inativa */}
                        {!statusPesquisa?.ativa && isAdminOrRH && (
                            <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '14px', marginBottom: '20px', fontSize: '13px', color: '#92400e' }}>
                                ℹ️ A pesquisa está inativa. Os envios automáticos por WhatsApp só ocorrerão quando a pesquisa estiver ativa. Clique em <strong>Ativar Pesquisa</strong> para iniciar um novo período de coleta.
                            </div>
                        )}

                        {/* Filtros */}
                        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '16px', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                            <select value={filterLoja} onChange={e => setFilterLoja(e.target.value)} style={inputStyle}>
                                <option value="">Todas as Lojas</option>
                                {lojas.map(l => <option key={l} value={l}>{l}</option>)}
                            </select>

                            {/* Alternância de visualização: por loja / por empresa */}
                            <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden' }}>
                                {[{ k: 'loja', label: 'Por Loja' }, { k: 'empresa', label: 'Por Empresa' }].map(opt => (
                                    <button
                                        key={opt.k}
                                        onClick={() => setAgruparPor(opt.k)}
                                        style={{
                                            padding: '9px 16px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
                                            backgroundColor: agruparPor === opt.k ? '#7c3aed' : '#fff',
                                            color: agruparPor === opt.k ? '#fff' : '#475569'
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>

                            <span style={{ fontSize: '13px', color: '#64748b' }}>
                                {dados.length} {porEmpresaAtivo ? 'empresa(s)' : 'loja(s)'}
                            </span>
                        </div>

                        {/* Tabela por loja */}
                        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                            {(porEmpresaAtivo
                                                ? ['Empresa', 'Total Elegíveis', 'Enviados', 'Respondidos', 'Aguardando', 'Expirados', 'Não Enviados', 'Taxa Resposta', 'Meta 51%']
                                                : ['Loja', 'Empresa', 'Total Elegíveis', 'Enviados', 'Respondidos', 'Aguardando', 'Expirados', 'Não Enviados', 'Taxa Resposta', 'Meta 51%']
                                            ).map((h, i) => (
                                                <th key={h} style={{ padding: '12px 14px', textAlign: i === 0 ? 'left' : 'center', fontWeight: '600', color: '#475569', whiteSpace: 'nowrap', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dados.length === 0 ? (
                                            <tr>
                                                <td colSpan={porEmpresaAtivo ? 9 : 10} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                                    Nenhum registro encontrado.
                                                </td>
                                            </tr>
                                        ) : dados.map((row, idx) => {
                                            const enviados = row.respondidos + row.aguardando + row.expirados;
                                            const taxa = enviados > 0 ? Math.round((row.respondidos / enviados) * 100) : 0;
                                            const metaAtingida = taxa >= 51;
                                            return (
                                                <tr key={row.LOJA || row.EMPRESA} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                    <td style={{ padding: '11px 14px', fontWeight: '700', color: '#334155' }}>{porEmpresaAtivo ? row.EMPRESA : row.LOJA}</td>
                                                    {!porEmpresaAtivo && (
                                                        <td style={{ padding: '11px 14px', color: '#64748b', textAlign: 'center', whiteSpace: 'nowrap' }}>{row.EMPRESA}</td>
                                                    )}
                                                    <td style={{ padding: '11px 14px', color: '#64748b', textAlign: 'center' }}>{row.total}</td>
                                                    <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                                                        <span style={{ fontWeight: '600', color: '#1e40af' }}>{enviados}</span>
                                                    </td>
                                                    <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                                                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '600', backgroundColor: '#d1fae5', color: '#065f46' }}>
                                                            {row.respondidos}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                                                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '600', backgroundColor: '#dbeafe', color: '#1e40af' }}>
                                                            {row.aguardando}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                                                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '600', backgroundColor: row.expirados > 0 ? '#fee2e2' : '#f1f5f9', color: row.expirados > 0 ? '#991b1b' : '#94a3b8' }}>
                                                            {row.expirados}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                                                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '600', backgroundColor: '#f1f5f9', color: '#475569' }}>
                                                            {row.pendentes}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                                                        <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '700', backgroundColor: metaAtingida ? '#d1fae5' : taxa >= 40 ? '#fef3c7' : '#fee2e2', color: metaAtingida ? '#065f46' : taxa >= 40 ? '#92400e' : '#991b1b' }}>
                                                            {taxa}%
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                                                        {enviados === 0 ? (
                                                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>—</span>
                                                        ) : metaAtingida ? (
                                                            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '700', backgroundColor: '#d1fae5', color: '#065f46' }}>Atingida</span>
                                                        ) : (
                                                            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: '700', backgroundColor: '#fee2e2', color: '#991b1b' }}>Não atingida</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}

export default PesquisaClimaConsulta;
